<template>
    <v-dialog
        :model-value="isOpen"
        max-width="600px"
        transition="slide-y-transition"
        eager
        @update:model-value="$emit('update:isOpen', $event)"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    {{ t('configurations.products.pages.edit.sections.components.assignComponent') }}
                </h1>
            </v-card-title>

            <v-card-text>
                <v-autocomplete
                    v-model="selectedComponent"
                    :items="productComponents"
                    :label="t('configurations.products.pages.edit.sections.components.selectComponent')"
                    :placeholder="t('configurations.products.pages.edit.sections.components.selectComponent')"
                    :filter="customComponentsFilter"
                    item-title="name"
                    item-value="id"
                    return-object
                    variant="outlined"
                    hide-details
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn color="error" variant="text" @click="onClose">
                    {{ t('configurations.products.pages.edit.sections.components.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn color="success" variant="text" :disabled="!hasSelectedComponent" @click="onSave">
                    {{ t('configurations.products.pages.edit.sections.components.add') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// 2. Props / Emits
defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    productComponents: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['on-save', 'on-close', 'update:isOpen'])

// 3. Constants
const { t } = useI18n()

// 4. Stores & top-level services
// No stores needed for this dialog

// 5. Reactive state (refs/reactive)
const selectedComponent = ref(null)

// 6. Validation rules & setup
// No validation rules needed

// 7. Computed
const hasSelectedComponent = computed(() => {
    return !!(selectedComponent.value && selectedComponent.value.id)
})

// 8. Handlers / methods
const customComponentsFilter = (item, queryText) => {
    const name = item.name.toLowerCase()
    const search = queryText.toLowerCase()
    return name.indexOf(search) > -1
}

const onSave = () => {
    emit('on-save', selectedComponent.value)
    onClose()
}

const onClose = () => {
    selectedComponent.value = null
    emit('on-close')
    emit('update:isOpen', false)
}

// 9. Helpers / lifecycle
// No lifecycle hooks needed
</script>
